import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

import { CallNoteViewPage } from '@karehero/llama/pwa/pages/CallNoteViewPage/CallNoteViewPage';
import type { PortableTextContent } from '@karehero/llama/components/organisms/RichTextEditor/portableText';

import { useGoBack } from 'hooks/useGoBack';

import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import { useGetOrganiseDocumentQuery } from 'store/api/organiseDocument';
import { DYNAMIC_ROUTES } from 'router/routes';

interface CallNoteContent {
  title: string;
  content: PortableTextContent;
  createdAt: string;
}

export const CallNoteView = () => {
  const goBack = useGoBack();
  const navigate = useNavigate();
  const { careCircleID, documentID } = useParams<{
    careCircleID?: string;
    documentID: string;
  }>();
  const careCircle = useSelector(getCurrentCareCircle);
  const careCircleId = careCircleID ?? careCircle?.id ?? '';

  const isAdminContext = !!careCircleID;

  const handleEdit = useCallback(() => {
    if (careCircleID && documentID) {
      navigate(DYNAMIC_ROUTES.ADMIN_CALL_NOTE_EDIT(careCircleID, documentID));
    }
  }, [careCircleID, documentID, navigate]);

  const [noteContent, setNoteContent] = useState<CallNoteContent | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);

  const { data: document, isLoading: isDocumentLoading } =
    useGetOrganiseDocumentQuery(
      { careCircleId, documentId: documentID ?? '' },
      { skip: !careCircleId || !documentID },
    );

  useEffect(() => {
    const fetchContent = async () => {
      if (!document?.downloadUrl) return;

      setIsLoadingContent(true);

      try {
        const response = await fetch(document.downloadUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch call note content');
        }
        const content: CallNoteContent = await response.json();
        setNoteContent(content);
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsLoadingContent(false);
      }
    };

    fetchContent();
  }, [document?.downloadUrl]);

  const isLoading = isDocumentLoading || isLoadingContent;

  return (
    <CallNoteViewPage
      title={noteContent?.title ?? document?.title ?? 'Call Note'}
      content={noteContent?.content}
      createdAt={noteContent?.createdAt ?? document?.createdAt}
      isLoading={isLoading}
      onBack={goBack}
      onEdit={isAdminContext ? handleEdit : undefined}
    />
  );
};

export default CallNoteView;
